import _ from 'lodash';
import axios from 'axios';

export default class ApiService {

  constructor(config = {}) {

    this.inProgress = {};
    this.maxDepth = config.maxDepth || 3;
    this.preserveCasing = config.preserveCasing || [];
    this.token = config.token;
    this.unauthenticated = config.unauthenticated;

    this.axios = axios.create({
      ...config,
    });

    this.axios.interceptors.response.use(null, async (error) => {

      if (error.response && error.response.status === 401) {

        const unauthenticated = this.unauthenticated || window.app.auth.unauthenticated || null;

        if (unauthenticated) {
          return unauthenticated();
        } else {
          return Promise.reject(error);
        }

      } else {
        return Promise.reject(error);
      }
    });
  }

  async request(config = {}) {

    const {headers, ...rest} = config;
    const baseUrl = config.apiBaseUrl || window.app.env.apiBaseUrl || '';

    let token = null;

    if (this.token) {
      token = this.token();
    } else if (window.app.auth.getToken) {
      token = await window.app.auth.getToken();
    }

    const requestHeaders = {
      Authorization: token ? `Bearer ${token}` : null,
      ...headers,
    };

    const requestPayload = {
      headers: requestHeaders,
      baseURL: baseUrl || '',
      responseType: 'json',
      ...rest,
    };

    const requestKey = this.unicodeSafeBase64(JSON.stringify(requestPayload));

    if (this.inProgress[requestKey]) {
      console.log('Request in progress. Skipping duplicate.');
      return this.inProgress[requestKey];
    }

    this.inProgress[requestKey] = this.axios(requestPayload).finally(() => {
      delete this.inProgress[requestKey];
    });

    return this.inProgress[requestKey];
  }

  prepareParams(params) {

    if (params && Array.isArray(params.include)) {
      params.include = params.include.join(',');
    }

    if (params && Array.isArray(params.require)) {
      params.require = params.require.join(',');
    }

    if (params && Array.isArray(params.with)) {
      params.with = params.with.join(',');
    }

    return params;
  }

  preparePayload(data, currentDepth = 0, preserveCasing = false) {

    const maxDepth = this.maxDepth || window.app.env.apiMaxDepth || 3;

    if (currentDepth >= maxDepth) {
      return data;
    }

    if (Array.isArray(data)) {
      return data.map(item => this.preparePayload(item, currentDepth + 1));
    } else if (typeof data === 'string' || data === null) {
      return data;
    } else if (typeof data === 'object') {

      let snaked = {};

      for (let key in data) {
        if (data.hasOwnProperty(key)) {

          if (this.preserveCasing.includes(key) || preserveCasing) {
            snaked[key] = this.preparePayload(data[key], currentDepth + 1);
          } else {
            snaked[_.snakeCase(key)] = this.preparePayload(data[key], currentDepth + 1);
          }

        }
      }

      return snaked;
    }

    return data;
  }

  unicodeSafeBase64(str) {
    // Convert Unicode string to UTF-8 bytes, then encode to base64
    try {
      // Use TextEncoder if available (modern browsers)
      if (typeof TextEncoder !== 'undefined') {
        const encoder = new TextEncoder();
        const utf8Bytes = encoder.encode(str);
        // Convert Uint8Array to binary string without using spread operator
        let binaryString = '';
        for (let i = 0; i < utf8Bytes.length; i++) {
          binaryString += String.fromCharCode(utf8Bytes[i]);
        }
        return btoa(binaryString);
      }
      
      // Fallback: use encodeURIComponent method
      return btoa(
        encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (match, p1) => {
          return String.fromCharCode(parseInt(p1, 16));
        })
      );
    } catch (e) {
      // Fallback: use a simple hash if encoding fails
      let hash = 0;
      for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32bit integer
      }
      return hash.toString(36);
    }
  }
}
